/**
 * Sistema de colores predefinidos para vehículos.
 * 
 * Cada color tiene:
 * - Un identificador único para el backend (en inglés, con variantes si aplica)
 * - Un color base para mostrar en el selector
 * - Colores adaptados para cada tema de la app (degradados sutiles)
 */
package link.erpango.quip.theme

import androidx.compose.runtime.Composable
import androidx.compose.ui.graphics.Color
import org.jetbrains.compose.resources.stringResource
import quip.composeapp.generated.resources.*

/**
 * Representa un color de vehículo con su identificador para backend y color base.
 */
data class VehicleColor(
    val id: String,           // Identificador único para backend (ej: "blue_dark", "red_metallic")
    val baseColor: Color,     // Color base para mostrar en selector y previews
    val nameResKey: String    // Clave del recurso de string para traducción
)

/**
 * Colores predefinidos para vehículos.
 * Lista amplia de colores comunes en la industria automotriz.
 */
object VehicleColors {
    
    // === BLANCOS Y GRISES ===
    val WHITE = VehicleColor("white", Color(0xFFFFFFFF), "vehicle_color_white")
    val WHITE_PEARL = VehicleColor("white_pearl", Color(0xFFF5F5F0), "vehicle_color_white_pearl")
    val IVORY = VehicleColor("ivory", Color(0xFFFFFFF0), "vehicle_color_ivory")
    
    val SILVER = VehicleColor("silver", Color(0xFFC0C0C0), "vehicle_color_silver")
    val SILVER_METALLIC = VehicleColor("silver_metallic", Color(0xFFB8B8B8), "vehicle_color_silver_metallic")
    val GREY = VehicleColor("grey", Color(0xFF808080), "vehicle_color_grey")
    val GREY_DARK = VehicleColor("grey_dark", Color(0xFF505050), "vehicle_color_grey_dark")
    val GRAPHITE = VehicleColor("graphite", Color(0xFF383838), "vehicle_color_graphite")
    val ANTHRACITE = VehicleColor("anthracite", Color(0xFF293133), "vehicle_color_anthracite")
    
    // === NEGROS ===
    val BLACK = VehicleColor("black", Color(0xFF000000), "vehicle_color_black")
    val BLACK_METALLIC = VehicleColor("black_metallic", Color(0xFF1C1C1C), "vehicle_color_black_metallic")
    
    // === AZULES ===
    val BLUE = VehicleColor("blue", Color(0xFF2196F3), "vehicle_color_blue")
    val BLUE_LIGHT = VehicleColor("blue_light", Color(0xFF64B5F6), "vehicle_color_blue_light")
    val BLUE_DARK = VehicleColor("blue_dark", Color(0xFF1565C0), "vehicle_color_blue_dark")
    val BLUE_NAVY = VehicleColor("blue_navy", Color(0xFF0D47A1), "vehicle_color_blue_navy")
    val BLUE_METALLIC = VehicleColor("blue_metallic", Color(0xFF1E88E5), "vehicle_color_blue_metallic")
    val BLUE_SKY = VehicleColor("blue_sky", Color(0xFF87CEEB), "vehicle_color_blue_sky")
    val TURQUOISE = VehicleColor("turquoise", Color(0xFF00BCD4), "vehicle_color_turquoise")
    val CYAN = VehicleColor("cyan", Color(0xFF00E5FF), "vehicle_color_cyan")
    
    // === ROJOS ===
    val RED = VehicleColor("red", Color(0xFFF44336), "vehicle_color_red")
    val RED_DARK = VehicleColor("red_dark", Color(0xFFB71C1C), "vehicle_color_red_dark")
    val RED_METALLIC = VehicleColor("red_metallic", Color(0xFFD32F2F), "vehicle_color_red_metallic")
    val BURGUNDY = VehicleColor("burgundy", Color(0xFF800020), "vehicle_color_burgundy")
    val MAROON = VehicleColor("maroon", Color(0xFF800000), "vehicle_color_maroon")
    val CHERRY = VehicleColor("cherry", Color(0xFFDE3163), "vehicle_color_cherry")
    
    // === VERDES ===
    val GREEN = VehicleColor("green", Color(0xFF4CAF50), "vehicle_color_green")
    val GREEN_LIGHT = VehicleColor("green_light", Color(0xFF81C784), "vehicle_color_green_light")
    val GREEN_DARK = VehicleColor("green_dark", Color(0xFF2E7D32), "vehicle_color_green_dark")
    val GREEN_METALLIC = VehicleColor("green_metallic", Color(0xFF388E3C), "vehicle_color_green_metallic")
    val OLIVE = VehicleColor("olive", Color(0xFF808000), "vehicle_color_olive")
    val KHAKI = VehicleColor("khaki", Color(0xFFC3B091), "vehicle_color_khaki")
    val TEAL = VehicleColor("teal", Color(0xFF009688), "vehicle_color_teal")
    
    // === AMARILLOS Y DORADOS ===
    val YELLOW = VehicleColor("yellow", Color(0xFFFFEB3B), "vehicle_color_yellow")
    val YELLOW_BRIGHT = VehicleColor("yellow_bright", Color(0xFFFFEA00), "vehicle_color_yellow_bright")
    val GOLD = VehicleColor("gold", Color(0xFFFFD700), "vehicle_color_gold")
    val GOLD_METALLIC = VehicleColor("gold_metallic", Color(0xFFD4AF37), "vehicle_color_gold_metallic")
    val CHAMPAGNE = VehicleColor("champagne", Color(0xFFF7E7CE), "vehicle_color_champagne")
    
    // === NARANJAS ===
    val ORANGE = VehicleColor("orange", Color(0xFFFF9800), "vehicle_color_orange")
    val ORANGE_DARK = VehicleColor("orange_dark", Color(0xFFE65100), "vehicle_color_orange_dark")
    val ORANGE_METALLIC = VehicleColor("orange_metallic", Color(0xFFFF5722), "vehicle_color_orange_metallic")
    val COPPER = VehicleColor("copper", Color(0xFFB87333), "vehicle_color_copper")
    
    // === MARRONES Y BEIGE ===
    val BROWN = VehicleColor("brown", Color(0xFF795548), "vehicle_color_brown")
    val BROWN_DARK = VehicleColor("brown_dark", Color(0xFF4E342E), "vehicle_color_brown_dark")
    val BROWN_LIGHT = VehicleColor("brown_light", Color(0xFFA1887F), "vehicle_color_brown_light")
    val BEIGE = VehicleColor("beige", Color(0xFFF5F5DC), "vehicle_color_beige")
    val SAND = VehicleColor("sand", Color(0xFFC2B280), "vehicle_color_sand")
    val BRONZE = VehicleColor("bronze", Color(0xFFCD7F32), "vehicle_color_bronze")
    
    // === MORADOS Y VIOLETAS ===
    val PURPLE = VehicleColor("purple", Color(0xFF9C27B0), "vehicle_color_purple")
    val PURPLE_DARK = VehicleColor("purple_dark", Color(0xFF6A1B9A), "vehicle_color_purple_dark")
    val VIOLET = VehicleColor("violet", Color(0xFF7B1FA2), "vehicle_color_violet")
    val LAVENDER = VehicleColor("lavender", Color(0xFFE6E6FA), "vehicle_color_lavender")
    val PLUM = VehicleColor("plum", Color(0xFF8E4585), "vehicle_color_plum")
    
    // === ROSAS ===
    val PINK = VehicleColor("pink", Color(0xFFE91E63), "vehicle_color_pink")
    val PINK_LIGHT = VehicleColor("pink_light", Color(0xFFF8BBD9), "vehicle_color_pink_light")
    val MAGENTA = VehicleColor("magenta", Color(0xFFFF00FF), "vehicle_color_magenta")
    
    /**
     * Lista completa de todos los colores disponibles, ordenados por categoría.
     */
    val allColors: List<VehicleColor> = listOf(
        // Blancos y grises
        WHITE, WHITE_PEARL, IVORY,
        SILVER, SILVER_METALLIC, GREY, GREY_DARK, GRAPHITE, ANTHRACITE,
        // Negros
        BLACK, BLACK_METALLIC,
        // Azules
        BLUE, BLUE_LIGHT, BLUE_DARK, BLUE_NAVY, BLUE_METALLIC, BLUE_SKY, TURQUOISE, CYAN,
        // Rojos
        RED, RED_DARK, RED_METALLIC, BURGUNDY, MAROON, CHERRY,
        // Verdes
        GREEN, GREEN_LIGHT, GREEN_DARK, GREEN_METALLIC, OLIVE, KHAKI, TEAL,
        // Amarillos y dorados
        YELLOW, YELLOW_BRIGHT, GOLD, GOLD_METALLIC, CHAMPAGNE,
        // Naranjas
        ORANGE, ORANGE_DARK, ORANGE_METALLIC, COPPER,
        // Marrones y beige
        BROWN, BROWN_DARK, BROWN_LIGHT, BEIGE, SAND, BRONZE,
        // Morados y violetas
        PURPLE, PURPLE_DARK, VIOLET, LAVENDER, PLUM,
        // Rosas
        PINK, PINK_LIGHT, MAGENTA
    )

    /**
     * Obtiene el nombre traducido de un color a partir de su key (nameResKey).
     */
    @Composable
    fun getStringFromKey(key: String): String {
        return when (key) {
            // Blancos y grises
            "vehicle_color_white" -> stringResource(Res.string.vehicle_color_white)
            "vehicle_color_white_pearl" -> stringResource(Res.string.vehicle_color_white_pearl)
            "vehicle_color_ivory" -> stringResource(Res.string.vehicle_color_ivory)
            "vehicle_color_silver" -> stringResource(Res.string.vehicle_color_silver)
            "vehicle_color_silver_metallic" -> stringResource(Res.string.vehicle_color_silver_metallic)
            "vehicle_color_grey" -> stringResource(Res.string.vehicle_color_grey)
            "vehicle_color_grey_dark" -> stringResource(Res.string.vehicle_color_grey_dark)
            "vehicle_color_graphite" -> stringResource(Res.string.vehicle_color_graphite)
            "vehicle_color_anthracite" -> stringResource(Res.string.vehicle_color_anthracite)
            
            // Negros
            "vehicle_color_black" -> stringResource(Res.string.vehicle_color_black)
            "vehicle_color_black_metallic" -> stringResource(Res.string.vehicle_color_black_metallic)
            
            // Azules
            "vehicle_color_blue" -> stringResource(Res.string.vehicle_color_blue)
            "vehicle_color_blue_light" -> stringResource(Res.string.vehicle_color_blue_light)
            "vehicle_color_blue_dark" -> stringResource(Res.string.vehicle_color_blue_dark)
            "vehicle_color_blue_navy" -> stringResource(Res.string.vehicle_color_blue_navy)
            "vehicle_color_blue_metallic" -> stringResource(Res.string.vehicle_color_blue_metallic)
            "vehicle_color_blue_sky" -> stringResource(Res.string.vehicle_color_blue_sky)
            "vehicle_color_turquoise" -> stringResource(Res.string.vehicle_color_turquoise)
            "vehicle_color_cyan" -> stringResource(Res.string.vehicle_color_cyan)
            
            // Rojos
            "vehicle_color_red" -> stringResource(Res.string.vehicle_color_red)
            "vehicle_color_red_dark" -> stringResource(Res.string.vehicle_color_red_dark)
            "vehicle_color_red_metallic" -> stringResource(Res.string.vehicle_color_red_metallic)
            "vehicle_color_burgundy" -> stringResource(Res.string.vehicle_color_burgundy)
            "vehicle_color_maroon" -> stringResource(Res.string.vehicle_color_maroon)
            "vehicle_color_cherry" -> stringResource(Res.string.vehicle_color_cherry)
            
            // Verdes
            "vehicle_color_green" -> stringResource(Res.string.vehicle_color_green)
            "vehicle_color_green_light" -> stringResource(Res.string.vehicle_color_green_light)
            "vehicle_color_green_dark" -> stringResource(Res.string.vehicle_color_green_dark)
            "vehicle_color_green_metallic" -> stringResource(Res.string.vehicle_color_green_metallic)
            "vehicle_color_olive" -> stringResource(Res.string.vehicle_color_olive)
            "vehicle_color_khaki" -> stringResource(Res.string.vehicle_color_khaki)
            "vehicle_color_teal" -> stringResource(Res.string.vehicle_color_teal)
            
            // Amarillos y dorados
            "vehicle_color_yellow" -> stringResource(Res.string.vehicle_color_yellow)
            "vehicle_color_yellow_bright" -> stringResource(Res.string.vehicle_color_yellow_bright)
            "vehicle_color_gold" -> stringResource(Res.string.vehicle_color_gold)
            "vehicle_color_gold_metallic" -> stringResource(Res.string.vehicle_color_gold_metallic)
            "vehicle_color_champagne" -> stringResource(Res.string.vehicle_color_champagne)
            
            // Naranjas
            "vehicle_color_orange" -> stringResource(Res.string.vehicle_color_orange)
            "vehicle_color_orange_dark" -> stringResource(Res.string.vehicle_color_orange_dark)
            "vehicle_color_orange_metallic" -> stringResource(Res.string.vehicle_color_orange_metallic)
            "vehicle_color_copper" -> stringResource(Res.string.vehicle_color_copper)
            
            // Marrones y beige
            "vehicle_color_brown" -> stringResource(Res.string.vehicle_color_brown)
            "vehicle_color_brown_dark" -> stringResource(Res.string.vehicle_color_brown_dark)
            "vehicle_color_brown_light" -> stringResource(Res.string.vehicle_color_brown_light)
            "vehicle_color_beige" -> stringResource(Res.string.vehicle_color_beige)
            "vehicle_color_sand" -> stringResource(Res.string.vehicle_color_sand)
            "vehicle_color_bronze" -> stringResource(Res.string.vehicle_color_bronze)
            
            // Morados y violetas
            "vehicle_color_purple" -> stringResource(Res.string.vehicle_color_purple)
            "vehicle_color_purple_dark" -> stringResource(Res.string.vehicle_color_purple_dark)
            "vehicle_color_violet" -> stringResource(Res.string.vehicle_color_violet)
            "vehicle_color_lavender" -> stringResource(Res.string.vehicle_color_lavender)
            "vehicle_color_plum" -> stringResource(Res.string.vehicle_color_plum)
            
            // Rosas
            "vehicle_color_pink" -> stringResource(Res.string.vehicle_color_pink)
            "vehicle_color_pink_light" -> stringResource(Res.string.vehicle_color_pink_light)
            "vehicle_color_magenta" -> stringResource(Res.string.vehicle_color_magenta)
            
            // Selector
            "vehicle_color_select" -> stringResource(Res.string.vehicle_color_select)
            
            // Fallback: devolver la key formateada si no se encuentra
            else -> key.removePrefix("vehicle_color_")
                .replace("_", " ")
                .replaceFirstChar { it.uppercase() }
        }
    }
    
    /**
     * Encuentra un color por su ID de backend.
     * Retorna WHITE si no encuentra el color.
     */
    fun fromId(id: String): VehicleColor {
        return allColors.find { it.id == id } ?: WHITE
    }
    
    /**
     * Intenta mapear un nombre de color en cualquier idioma al VehicleColor correspondiente.
     * Útil para migrar datos existentes o interpretar colores escritos manualmente.
     */
    fun fromName(name: String): VehicleColor {
        val normalized = name.lowercase().replace("_", " ").trim()
        return when {
            // Blancos
            normalized in listOf("white", "blanco", "blanc", "weiß", "weiss") -> WHITE
            normalized in listOf("pearl white", "blanco perla", "blanco perlado") -> WHITE_PEARL
            normalized in listOf("ivory", "marfil") -> IVORY
            
            // Plateados y grises
            normalized in listOf("silver", "plata", "plateado", "argent", "silber") -> SILVER
            normalized in listOf("grey", "gray", "gris", "grau") -> GREY
            normalized in listOf("dark grey", "dark gray", "gris oscuro") -> GREY_DARK
            normalized in listOf("graphite", "grafito") -> GRAPHITE
            normalized in listOf("anthracite", "antracita") -> ANTHRACITE
            
            // Negros
            normalized in listOf("black", "negro", "noir", "schwarz") -> BLACK
            
            // Azules
            normalized in listOf("blue", "azul", "bleu", "blau") -> BLUE
            normalized in listOf("light blue", "azul claro", "azul celeste", "celeste") -> BLUE_LIGHT
            normalized in listOf("dark blue", "azul oscuro") -> BLUE_DARK
            normalized in listOf("navy", "navy blue", "azul marino") -> BLUE_NAVY
            normalized in listOf("sky blue", "azul cielo") -> BLUE_SKY
            normalized in listOf("turquoise", "turquesa") -> TURQUOISE
            normalized in listOf("cyan", "cian") -> CYAN
            
            // Rojos
            normalized in listOf("red", "rojo", "rouge", "rot") -> RED
            normalized in listOf("dark red", "rojo oscuro") -> RED_DARK
            normalized in listOf("burgundy", "burdeos", "borgoña") -> BURGUNDY
            normalized in listOf("maroon", "granate") -> MAROON
            normalized in listOf("cherry", "cereza") -> CHERRY
            
            // Verdes
            normalized in listOf("green", "verde", "vert", "grün", "grun") -> GREEN
            normalized in listOf("light green", "verde claro") -> GREEN_LIGHT
            normalized in listOf("dark green", "verde oscuro") -> GREEN_DARK
            normalized in listOf("olive", "oliva") -> OLIVE
            normalized in listOf("khaki", "caqui") -> KHAKI
            normalized in listOf("teal") -> TEAL
            
            // Amarillos y dorados
            normalized in listOf("yellow", "amarillo", "jaune", "gelb") -> YELLOW
            normalized in listOf("gold", "dorado", "oro") -> GOLD
            normalized in listOf("champagne", "champán", "champan") -> CHAMPAGNE
            
            // Naranjas
            normalized in listOf("orange", "naranja") -> ORANGE
            normalized in listOf("copper", "cobre") -> COPPER
            
            // Marrones y beige
            normalized in listOf("brown", "marrón", "marron", "café", "cafe") -> BROWN
            normalized in listOf("beige", "beis") -> BEIGE
            normalized in listOf("sand", "arena") -> SAND
            normalized in listOf("bronze", "bronce") -> BRONZE
            
            // Morados
            normalized in listOf("purple", "morado", "púrpura", "purpura", "violet", "violeta") -> PURPLE
            normalized in listOf("lavender", "lavanda") -> LAVENDER
            normalized in listOf("plum", "ciruela") -> PLUM
            
            // Rosas
            normalized in listOf("pink", "rosa", "rosado") -> PINK
            normalized in listOf("magenta", "fucsia", "fuchsia") -> MAGENTA
            
            else -> WHITE // Color por defecto si no se encuentra
        }
    }
}
